import { ESnapshotExists, EOrganization, EUser } from '.';

type FeatureFlagOptionsBase<FlagType> = {
  defaultValue: FlagType;
};

/**
 * Subset of a LaunchDarkly user object.
 */
export interface CustomLDUser {
  /**
   * A unique string identifying a user.
   */
  key: string;

  /**
   * Any additional attributes associated with the user.
   */
  custom?: {
    [key: string]: string | boolean | number | Array<string | boolean | number>;
  };
}

type CustomFeatureFlagOptions<FlagType> = FeatureFlagOptionsBase<FlagType> & {
  type: 'custom';
  user: CustomLDUser;
};

type NoSnapshotFeatureFlagOptions<FlagType> =
  FeatureFlagOptionsBase<FlagType> & {
    type: 'anonymous';
  };

// Used for percentage-based rollouts in the BE where we need each request to have a unique key
type RandomizedKeyFeatureFlagOptions<FlagType> =
  FeatureFlagOptionsBase<FlagType> & {
    type: 'randomized';
  };

type UserFeatureFlagOptions<FlagType> = FeatureFlagOptionsBase<FlagType> & {
  type: 'user';
  snapshot: ESnapshotExists<EUser>;
};

type OrganizationFeatureFlagOptions<FlagType> =
  FeatureFlagOptionsBase<FlagType> & {
    type: 'organization';
    snapshot: ESnapshotExists<EOrganization>;
  };

export type FeatureFlagOptions<FlagType> =
  | NoSnapshotFeatureFlagOptions<FlagType>
  | UserFeatureFlagOptions<FlagType>
  | OrganizationFeatureFlagOptions<FlagType>
  | RandomizedKeyFeatureFlagOptions<FlagType>
  | CustomFeatureFlagOptions<FlagType>;

export enum LaunchDarklyFlags {
  /**
   * Enables new hyphenation settings for long sequences in notice content.
   * See: IT-4401 and ONCALL-1530
   */
  ENABLE_ADVANCED_HYPHENATION = 'enable-advanced-hyphenation',

  /**
   * Enables whether or not a publisher can edit the order number for an invoice
   */
  ENABLE_ORDER_NUMBER_EDITING = 'enable-order-number-editing',

  /**
   * Will allow for bulk pagination without ever hitting the Indesign server
   */
  ENABLE_TURBO_BULK_PAGINATION = 'enable-turbo-bulk-pagination',

  /**
   * Used for column express, where we redirect users through a typeform
   * to enter their newspaper information and proceed to file notices on their behalf
   * This will be toggled off (Feb 23) until we restructure Column Express
   */
  ENABLE_CANT_FIND_NEWSPAPER = 'enable-cant-find-newspaper',

  /*
   * Numeric feature flag. Returns number 0-100
   */
  INDESIGN_ERROR_SAMPLE_RATE = 'indesign-error-sample-rate',

  /**
   * Enable Account ID management in the placement flow
   */
  ENABLE_ACCOUNT_ID_IN_PLACEMENT = 'enable-account-id-in-placement',

  /**
   * Disable migrations (kill switch)
   */
  DISABLE_MIGRATIONS = 'disable-migrations',

  /**
   * Use Lee's production Adpoint
   */
  USE_LEE_PRODUCTION_ADPOINT = 'use-lee-production-adpoint',

  /**
   * PERMANENT FLAG for overriding signing info.
   */
  NOTARIZE_WHITE_TEXT_OVERRIDES = 'notarize-white-text-overrides',

  /**
   * Enable notice type editing in-app
   */
  ENABLE_NOTICE_TYPE_SETTINGS = 'enable-notice-type-settings',

  /**
  /*
   * Enable notice type editing in-app
   */
  PREVENT_NON_GOVERNMENT_CHECK_RECONCILIATION = 'prevent-non-government-check-reconciliation',

  /**
   * When turned on, Champion AMP ads clear (are not set on) setaside
   */
  ENABLE_CHAMPION_ADS_CLEAR_SETASIDE = 'enable_champion_ads_clear_setaside',

  /**
   * When turned on, we route to a new and improved invoice creation screen
   */
  ENABLE_NEW_CREATE_INVOICE_SCREEN = 'enable-new-create-invoice-screen',

  /**
   * When turned on, add a buffer to the newspaper deadline in notice placement flow
   */
  ENABLE_NOTICE_PLACEMENT_DEADLINE_BUFFER = 'enable-notice-placement-deadline-buffer',

  /**
   * When turned on, the sync error table will be available and the icon will appear in the app header
   */
  ENABLE_SYNC_ERROR_TABLE = 'enable-sync-error-table',

  /**
   * Show the DisplayAdActions card on the notice details page
   */
  ENABLE_DISPLAY_AD_ACTIONS_CARD = 'enable-display-ad-actions-card',

  /**
   * Show modular size options in the placement flow for display ads
   */
  ENABLE_MODULAR_SIZES = 'enable-modular-sizes',

  /**
   * Enable using CloudConvert instead of the Indesign Server for bulk pagination RTF
   */
  ENABLE_BULK_RTF_CLOUDCONVERT = 'enable-bulk-rtf-cloud-convert',

  /*
   * Used to limit indesign calls during AA
   */
  SKIP_INDESIGN_FOR_AFFIDAVIT_GENERATION = 'skip_indesign_for_affidavit_generation',

  /** Enable the user to put the notice in a "Reviewing" status */
  ENABLE_AWAITING_CONFIRMATION_STATUS = 'enable-awaiting-confirmation-status',

  /**
   * Show in-app docs rendered via Markdown
   */
  ENABLE_IN_APP_DOCS = 'enable-in-app-docs',

  /**
   * TEMPORARY FLAG for enabling SDUT merchant account attribution in Payway card transactions.
   * If false, we default to using the test SDUT Payway account.
   */
  ENABLE_SDUT_PROD_PAYWAY = 'enable-sdut-prod-payway',

  /**
   * Enable in-app pagination review
   */
  ENABLE_IN_APP_PAGINATION_REVIEW = 'enable-in-app-pagination-review',

  /**
   * When enabled, will re-size unsquashed tables in the bulk download file to fit the columns in the template.
   * Currently behind a flag as the method to do that was written specifically for Forum and needs further testing
   * before we're comfortable rolling out to all publishers.
   */
  ADJUST_TABLE_WIDTHS_IN_BULK_DOWNLOAD_FILE = 'adjust-table-widths-in-bulk-download-file',

  /**
   * An experimental flag which keeps <table> tags in TinyMCE editor
   */
  ENABLE_TABLE_PASTING_IN_NOTICE_EDITOR = 'enable-table-pasting-in-notice-editor',

  /**
   * Allows users to see the TinyMCE html 'code'
   */
  ENABLE_TINY_MCE_SOURCE_CODE = 'enable-tiny-mce-source-code',

  /**
   * When enabled, PDF document to text conversion in the palcement flow will
   * use cloud convert (pdf to docx to html) instead of OCR
   */
  ENABLE_PDF_TO_HTML_CLOUD_CONVERT = 'enable-pdf-to-html-cloud-convert',

  /**
   * When enabled, stop auto-increase column width of notice and instead show user a message to increase
   * column width to fit the content.
   */
  STOP_AUTO_INCREASE_COLUMN_WIDTH = 'stop-auto-increase-column-width',

  /**
   * When enabled, we don't send the <AMPACCTNBR/> tag in the XML. This was causing (purportedly) duplication of ads in Champion for TL but not the other papers
   */
  CHAMPION_WITHHOLD_AMPACCTNBR_FROM_XML = 'champion-withhold-ampacctnbr-from-xml',

  /**
   * Can be used to override the default processor config for the processEEditionUploadedEventsCron
   */
  E_EDITION_UPLOADED_EVENT_PROCESSOR_CONFIG = 'e-edition-uploaded-event-processor-config',

  /**
   * Can be used to override the default processor config for the processNoticeReconcileRunsEventsCron
   */
  NOTICE_RECONCILE_RUNS_EVENT_PROCESSOR_CONFIG = 'notice-reconcile-runs-event-processor-config',

  /**
   * Can be used to override the default processor config for the processRunVerificationInitiatedEventsCron
   */
  RUN_VERIFICATION_EVENT_PROCESSOR_CONFIG = 'run-verification-event-processor-config',

  /**
   * Enable attachment of PDF of unpaid invoices report to each new invoice notification
   */
  ENABLE_ADD_OF_UNPAID_INVOICES_REPORT_TO_NEW_INVOICE_NOTIFICATIONS = 'enable-add-of-unpaid-invoices-report-to-new-invoice-notifications',

  /**
   * When turned on, all Elastic requests will be performed with the new Elastic node client @elastic/enterprise-search
   */
  USE_NEW_ELASTIC_CLIENT = 'use-new-elastic-client',

  /**
   * When enabled, payments through Elavon for Lee papers using Adpoint will use a surcharged-enabled terminal (which will apply surcharges for CCs that accept surcharges)
   */
  ENABLE_LEE_ADPOINT_SURCHARGES_IN_ELAVON = 'enable-lee-adpoint-surcharges-in-elavon',

  /**
   * Set max number of affidavits that can go in a single auto-affidavit signing batch
   */
  AUTO_AFFIDAVIT_SIGNING_BATCH_SIZE = 'AUTO_AFFIDAVIT_SIGNING_BATCH_SIZE',

  /**
   * Use Column's CDN and image transformer
   */
  ENABLE_COLUMN_CDN = 'enable-column-cdn',

  /**
   * When enabled the notice category filter will be shown in the notices table
   */
  ENABLE_NOTICE_CATEGORY_FILTER = 'enable-notice-category-filter',

  /**
   * Show multiple publishing mediums in order settings for enabled organizations
   */
  ENABLE_PUBLISHING_MEDIUMS_SETTINGS = 'enable-publishing-mediums-settings',

  /**
   * Override emails from a Column user with an email-specific string set in LD.
   */
  INBOX_AUTOMATION_EMAIL_OVERRIDE = 'inbox-automation-email-override',

  /**
   * Maps sender email addresses to Column user emails for inbox automation placement.
   * Used to map external sender handles to the appropriate Column user account.
   */
  INBOX_AUTOMATION_EMAIL_MAPPING = 'inbox-automation-email-mapping',

  /**
   * Enables uploading of zero-order publication issue manifest to the publisher via FTP from pagination
   */
  ENABLE_ZERO_ORDER_PUBLICATION_ISSUE_MANIFEST_UPLOAD_TO_PUBS_FROM_PAGINATION = 'enable-zero-order-publication-issue-manifest-upload-to-pubs-from-pagination',

  /**
   * Multipage PDFs downloaded in bulk download and notice details page instead of smashed versions
   */
  ENABLE_MULTIPAGE_PDF_DOWNLOADS = 'enable-multipage-pdf-downloads',

  /**
   *
   */
  ENABLE_ADJUDICATION_AREA_PLACEMENT_FILTER_CITY = 'adjudication-areas-placement-filter-city',

  /**
   * Enable the Copilot editor
   */
  ENABLE_COPILOT_EDITOR = 'enable-copilot-editor',

  /**
   * Enable the Pintura image editor
   */
  ENABLE_ADVANCED_IMAGE_EDITING = 'enable-advanced-image-editing',

  /**
   * Enable a faster version of the publisher select step in the placement flow
   */
  ENABLE_FAST_PUBLISHER_SELECTION = 'enable-fast-publisher-selection',

  /**
   * Enable Puppeteer rendering caching
   */
  ENABLE_PUPPETEER_RENDERING_CACHING = 'enable-puppeteer-rendering-caching',

  /**
   * Enable using concept of period-based rates into our billing for notices, obits and classifieds
   */
  ENABLE_PERIOD_BASED_ORDER_PRICING = 'enable-period-based-order-pricing',

  /**
   * Show a "universal download" button on the pagination table
   */
  ENABLE_UNIVERSAL_DOWNLOAD_BUTTON_ON_PAGINATION_TABLE = 'enable-universal-download-button-on-pagination-table',

  /**
   * Enables publisher fee splits for orders (obits and classifieds)
   */
  ENABLE_FEE_SPLITS_FOR_ORDERS = 'enable-fee-splits-for-orders',

  /**
   * Enable the new pending-transferStatus + cron-based notice transfer mechanism.
   * When false, legacy immediate transfer behavior is used and cron should be a no-op.
   * Temporary flag for phased rollout; remove after full adoption.
   */
  NEW_NOTICES_TRANSFER_MECHANISM = 'new-notices-transfer-mechanism',

  /**
   * Shift the order madlib questions to filing type selection step
   */
  SHIFT_ORDER_MADLIB_QUESTIONS_TO_FILING_TYPE_SELECTION = 'shift-order-madlib-questions-to-filing-type-selection',

  /**
   * Disable address verification for a specific user (useful for allowing mailing when USPS has not verified the address yet)
   */
  DISABLE_ADDRESS_VERIFICATION = 'disable-address-verification',

  /**
   * Enable / disable embeddings for orders in elasticsearch
   */
  ENABLE_SEARCHABLE_ORDER_EMBEDDINGS = 'enable-searchable-order-embeddings',

  /**
   * Enable controlling the Column public notice display site directly in the app
   */
  ENABLE_COLUMN_PUBLIC_NOTICE_DISPLAY_SITE_CONTROL = 'expose-column-public-notice-display-site-settings',

  /**
   * Enables configuration and payment for Jobcase upsell in orders
   */
  ENABLE_JOBCASE_UPSELL = 'enable-jobcase-upsell',

  /**
   * Persistent kill switch to disable inbox automation trigger events
   */
  DISABLE_INBOX_AUTOMATION_TRIGGER_EVENTS = 'disable-inbox-automation-trigger-events',

  /**
   * Enable the placements tab for express email conversations
   */
  ENABLE_PLACEMENTS_TAB = 'enable-placements-tab',

  /*
   * Enable Lee auto google drive upload
   */
  ENABLE_LEE_AUTO_GOOGLE_DRIVE_UPLOAD = 'enable-lee-auto-google-drive-upload',

  /**
   * Enable new elastic-based pagination issue section table
   */
  ENABLE_NEW_ELASTIC_PAGINATION_ISSUE_SECTION_TABLE = 'use-new-elastic-pagination-issue-section-table',

  /**
   * Enable new pagination settings
   */
  ENABLE_NEW_PAGINATION_SETTINGS = 'enable-new-pagination-settings',

  /**
   * Enable NYPA statutory rate pricing
   */
  ENABLE_NYPA_STATUTORY_RATE_PRICING = 'enable-nypa-statutory-rate-pricing',

  /**
   * Run auto pagination on publication issues when they change status
   */
  ENABLE_AUTO_PAGINATION_ON_PUBLICATION_ISSUE_STATUS_CHANGE = 'enable-auto-pagination-on-publication-issue-status-change',

  /**
   * Enable auto-uploading to in-app admin builder
   */
  ENABLE_AUTO_UPLOADING_TO_IN_APP_ADMIN_BUILDER = 'enable-auto-uploading-to-in-app-admin-builder',

  /**
   * Enable the tabbed invoices view on /payments with a Publisher Invoices tab
   * listing the organization's Column (Stripe subscription) invoices
   */
  ENABLE_PUBLISHER_INVOICE_TABLE = 'enable-publisher-invoice-table',

  /**
   * Enable box-sizing: content-box for the listing box
   */
  ENABLE_CONTENT_BOX_SIZING = 'enable-content-box-sizing',

  /**
   * Enable auto placement of approved AX ads
   */
  ENABLE_AUTO_PLACEMENT_OF_APPROVED_AX_ADS = 'enable-auto-placement-of-approved-ax-ads',

  /**
   * Enable Column Elastic-based reports
   */
  ENABLE_COLUMN_REPORTS = 'enable-column-reports-v-2',

  /**
   * Enable standalone Render Service on Cloud Run
   */
  ENABLE_STANDALONE_RENDER_SERVICE = 'enable-standalone-render-service',

  /**
   * Determines whether to use the custom parsing logic or generalized parsing
   * for demand-side partner notices ingested via email.
   */
  USE_GENERAL_DEMAND_SIDE_PARSER = 'use-general-demand-side-parser',

  /**
   * Enable eventual consistency for Elastic document deletions
   */
  ENABLE_ELASTIC_EVENTUAL_CONSISTENCY_FOR_DELETIONS = 'enable-elastic-eventual-consistency-for-deletions',

  /**
   * Speed factor to control the speed of the Stripe customers paginator
   */
  STRIPE_CUSTOMERS_PAGINATOR_SPEED_FACOTR = 'stripe-customers-paginator-speed-factor',

  /**
   * Enable differentiating between external and partner publishers in the placement flow
   */
  ENABLE_EXTERNAL_PUBLISHER_FILTER = 'enable-external-publisher-filter',

  /**
   * Numeric feature flag. Decrease the height
   */
  AFFINITYX_HEIGHT_DECREASE = 'affinityx-height-decrease',

  /**
   * Enable decoration of multipage display pdfs
   */
  ENABLE_DECORATION_OF_MULTIPAGE_DISPLAY_PDFS = 'enable-decoration-of-multipage-display-pdfs',

  /**
   * Enable auto-setting titles on death notices
   */
  ENABLE_AUTO_SETTING_TITLES_ON_DEATH_NOTICES = 'enable-auto-setting-titles-on-death-notices',

  /**
   * Enable showing advertiser logo in image gallery for order content
   */
  ENABLE_ORGANIZATION_LOGO_IN_ORDER_GALLERY = 'enable-organization-logo-in-order-gallery',

  /**
   * Show unverifiable notice digest in app notifications
   */
  SHOW_UNVERIFIABLE_NOTICE_DIGEST = 'show-unverifiable-notice-digest',

  /**
   * Enable Cloud Run infrastructure
   */
  USE_CLOUD_RUN = 'use-cloud-run',

  /**
   * Enable routing requests to the new API Cloud service
   */
  USE_CLOUD_RUN_API = 'use-cloud-run-api',

  /**
   * Enable routing requests to the new API Cloud service
   */
  USE_CLOUD_RUN_CLOUD_CONVERT = 'use-cloud-run-cloud-convert',

  /**
   * Enable routing requests to the new API Cloud service
   */
  USE_CLOUD_RUN_CREATE_PUBLISHER_DOCS_ON_CALL = 'use-cloud-run-create-publisher-docs-on-call',

  /**
   * Enable routing requests to the new API Cloud service
   */
  USE_CLOUD_RUN_CREATE_STRIPE_CUSTOMER = 'use-cloud-run-create-stripe-customer',

  /**
   * Use new Lob typescript SDK
   */
  USE_NEW_LOB_SDK = 'use-new-lob-sdk',

  /**
   * Enable increase of Oklahoma word and tabular rates
   */
  ENABLE_NEW_OKLAHOMA_RATE = 'enable-new-oklahoma-rate',

  /**
   * Email string for testing KASS parsers
   */
  KASS_EMAIL_IDENTIFIER = 'kass-email-identifier',

  /**
   * Enable background images in orders
   */
  ENABLE_BACKGROUND_IMAGES_IN_ORDERS = 'enable-background-images-in-orders',

  /**
   * Enable additive placement fee as a line item on the advertiser's Stripe invoice
   */
  ENABLE_DEMAND_SIDE_ADDITIVE_PLACEMENT_FEE = 'enable-demand-side-additive-placement-fee',

  /**
   * Enable customer-facing affidavit & invoice delivery add-ons (RON vs wet-sig
   * choice, shipping speed, per-copy pricing) in the placement flow. UI-only.
   */
  ENABLE_AFFIDAVIT_INVOICE_ADDONS = 'enable-affidavit-invoice-addons',

  /**
   * Enable caching of public notice facets
   */
  ENABLE_PUBLIC_NOTICE_FACET_CACHING = 'enable-public-notice-facet-caching',

  /**
   * Show the Run Verification app in Column admin
   */
  ADMIN_NEW_RUN_VERIFICATION_UI = 'admin-new-run-verification-ui',

  /**
   * Hours a queued OCR check or an unread e-edition upload keeps an OCR-failed
   * run out of the Run Verification list
   */
  RUN_VERIFICATION_OCR_PENDING_WINDOW_HOURS = 'run-verification-ocr-pending-window-hours',

  /**
   * ISO-8601 datetime with offset. Lee Naviga posts a payment only for an
   * invoice finalized at or after this instant
   *  Empty or invalid blocks every payment post.
   */
  LEE_NAVIGA_PAYMENT_SYNC_CUTOFF = 'lee-naviga-payment-sync-cutoff',

  /**
   * Kill switch for claiming Stripe invoice numbers on Column's notice and order
   * drafts. When on, new drafts stay unnumbered and Stripe numbers them at finalization.
   */
  DISABLE_DRAFT_INVOICE_NUMBER_CLAIMS = 'disable-draft-invoice-number-claims'
}

export interface LaunchDarklyContext {
  getBooleanFeatureFlag(
    flag: LaunchDarklyFlags,
    options?: FeatureFlagOptions<boolean>
  ): Promise<boolean>;
}
